import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { deleteUser } from '../../services/iam';
import { User } from '../../types/iam';
import { toast } from '@/lib/toast';
import { AlertTriangle } from 'lucide-react';
import { Button, Input, Modal } from '@/components/ds-zta';

interface DeleteUserModalProps {
  user: User;
  onClose: () => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ user, onClose }) => {
  const queryClient = useQueryClient();
  const [confirmEmail, setConfirmEmail] = useState('');

  const mutation = useMutation(
    () => deleteUser(user.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success(`User ${user.email} deleted successfully`);
        onClose();
      },
      onError: (err: any) => {
        const errorMessage = err.response?.data?.error || err.message;
        toast.error(`Failed to delete user: ${errorMessage}`);
      },
    }
  );

  const isConfirmed = confirmEmail === user.email;
  const isLoading = mutation.isLoading;

  const handleDelete = () => {
    if (!isConfirmed) {
      toast.error('Please confirm by typing the user email');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-[var(--danger-text)]" aria-hidden="true" />
          Delete User
        </span>
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={!isConfirmed || isLoading}>
            {isLoading ? 'Deleting...' : 'Delete User'}
          </Button>
        </>
      }
    >
      <div className="mb-4 p-4 bg-[var(--zta-tint-danger)] rounded-md border border-[var(--zta-tint-danger-border)]">
        <p className="text-sm text-[var(--danger-text)]">
          <strong>Warning:</strong> This action cannot be undone. All user data will be permanently deleted including:
        </p>
        <ul className="mt-2 ml-4 text-sm text-[var(--danger-text)] list-disc">
          <li>User account and profile</li>
          <li>User identities (d2auth, OAuth)</li>
          <li>Role assignments</li>
          <li>Group memberships</li>
          <li>User invites</li>
        </ul>
      </div>

      <div>
        <p className="text-sm text-[var(--d2d-text-secondary)] mb-2">
          User to delete: <span className="font-semibold text-[var(--d2d-text-primary)]">{user.email}</span>
        </p>
        <p className="text-sm text-[var(--d2d-text-secondary)] mb-3">
          To confirm deletion, please type the user email below:
        </p>
        <Input
          type="text"
          label="Type user email to confirm"
          hideLabel
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder="Type user email to confirm"
        />
      </div>
    </Modal>
  );
};

export default DeleteUserModal;
